import { usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { PayPalAcp3dsAction } from '../../../services/PayPal3ds';
import { useMst } from '../../../models/stores/Root';
import {
  PayPalAcpHostedFieldsCollection,
  PayPalAcpHostedFieldsData,
  PayPalAcpHostedFieldsInstance,
  PayPalAcpHostedFieldsResponseData,
  PayPalAcpHostedFieldsState,
  PayPalAcpMethods,
} from '../../../types/PayPal';
import { UIConstants } from '../../../utils/constants';
import PayPalAdvancedCardProcessingField from '../../input/PayPalAdvancedCardProcessingField';
import FormErrorLabel from '../../input/FormErrorLabel';

export const PayPalAdvancedCardProcessingTestId =
  'PayPalAdvancedCardProcessing-payment-input';

export type PayPalAdvancedCardProcessingProps = {
  acpMethods: PayPalAcpMethods;
};

type PayPalAdvancedCardProcessingBillingPayload = {
  cardholderName: string;
  billingAddress: {
    streetAddress: string;
    extendedAddress: string | undefined;
    region: string;
    locality: string;
    postalCode: string;
    countryCodeAlpha2: string;
  };
  contingencies?: string[];
};

export type PayPalAdvancedCardProcessingFieldState = {
  showPlaceholder: boolean;
  hasError: boolean;
};

const defaultFieldState: PayPalAdvancedCardProcessingFieldState = {
  showPlaceholder: true,
  hasError: false,
};

const PayPalAdvancedCardProcessing: React.FC<PayPalAdvancedCardProcessingProps> =
  observer(
    ({
      acpMethods: { onCardValidated },
    }: PayPalAdvancedCardProcessingProps) => {
      const rootStore = useMst();
      const { cart, configuration } = rootStore;
      const [{ isPending }] = usePayPalScriptReducer();
      const [formErrors, setFormErrors] = useState<string[]>([]);
      const [isRendered, setIsRendered] = useState(false);
      const [cardNumberState, setCardNumberState] =
        useState<PayPalAdvancedCardProcessingFieldState>(defaultFieldState);
      const [expDateState, setExpDateState] =
        useState<PayPalAdvancedCardProcessingFieldState>(defaultFieldState);
      const [cvvState, setCvvState] =
        useState<PayPalAdvancedCardProcessingFieldState>(defaultFieldState);

      let hostedFields: PayPalAcpHostedFieldsInstance;

      const isFieldReady = (fieldState: PayPalAcpHostedFieldsData): boolean => {
        return fieldState.isValid && !fieldState.isEmpty;
      };

      const isReadyToSubmit = (
        fieldStateCollection: PayPalAcpHostedFieldsCollection
      ): boolean => {
        const { number, expirationDate, cvv } = fieldStateCollection;
        return (
          isFieldReady(number) &&
          isFieldReady(expirationDate) &&
          isFieldReady(cvv)
        );
      };

      const translateDataToState = (
        fieldData: PayPalAcpHostedFieldsData
      ): PayPalAdvancedCardProcessingFieldState => {
        return {
          showPlaceholder: !fieldData.isFocused && fieldData.isEmpty,
          hasError:
            !fieldData.isFocused &&
            !fieldData.isPotentiallyValid &&
            !fieldData.isValid,
        };
      };
      const updateFieldStates = (
        fields: PayPalAcpHostedFieldsCollection
      ): void => {
        setCardNumberState(translateDataToState(fields.number));
        setExpDateState(translateDataToState(fields.expirationDate));
        setCvvState(translateDataToState(fields.cvv));
      };

      const handleFieldStates = (event: PayPalAcpHostedFieldsState): void => {
        updateFieldStates(event.fields);
      };

      const addCardFieldError = (
        fieldState: PayPalAcpHostedFieldsData,
        errors: { invalid: string; empty: string },
        errorMessages: string[]
      ): void => {
        if (fieldState.isEmpty) {
          errorMessages.push(errors.empty);
        } else if (!fieldState.isValid) {
          errorMessages.push(errors.invalid);
        }
      };

      const setCardFormErrors = (
        fieldStateCollection: PayPalAcpHostedFieldsCollection
      ): void => {
        const messages: string[] = [];
        addCardFieldError(
          fieldStateCollection.number,
          {
            empty: UIConstants.EMPTY_CARD_NUMBER,
            invalid: UIConstants.INVALID_CARD_NUMBER,
          },
          messages
        );
        addCardFieldError(
          fieldStateCollection.expirationDate,
          {
            empty: UIConstants.EMPTY_EXP_DATE,
            invalid: UIConstants.INVALID_EXP_DATE,
          },
          messages
        );
        addCardFieldError(
          fieldStateCollection.cvv,
          {
            empty: UIConstants.EMPTY_CVV,
            invalid: UIConstants.INVALID_CVV,
          },
          messages
        );
        setFormErrors(messages);
      };

      const handleSubmitCard = async (): Promise<void> => {
        setFormErrors([]);
        try {
          if (hostedFields) {
            const fieldState = hostedFields.getState();
            if (isReadyToSubmit(fieldState.fields)) {
              cart.setIsTokenizingCard(true);
              const { billingAddress } = cart;
              const cardholderName = `${billingAddress.firstName} ${billingAddress.lastName}`;
              const streetAddress = billingAddress.address1 as string;
              const extendedAddress = billingAddress.address2 || undefined;
              const region = billingAddress.state as string;
              const locality = billingAddress.city as string;
              const postalCode = billingAddress.postalCode as string;
              const countryCodeAlpha2 = configuration.findCountryIsoCode(
                billingAddress.country
              );
              const billingPayload: PayPalAdvancedCardProcessingBillingPayload =
                {
                  cardholderName,
                  billingAddress: {
                    streetAddress,
                    extendedAddress,
                    region,
                    locality,
                    postalCode,
                    countryCodeAlpha2,
                  },
                };
              if (configuration.paypalExpress.contingencies) {
                billingPayload.contingencies = [
                  configuration.paypalExpress.contingencies,
                ];
              }
              const result = await hostedFields.submit(billingPayload);
              const acp3DsAction = rootStore.getPayPal3dsAction(result);
              if (acp3DsAction === PayPalAcp3dsAction.Retry) {
                setFormErrors([
                  UIConstants.CARD_AUTHENTICATION_ERROR,
                  UIConstants.PROMPT_TRY_AGAIN,
                ]);
              } else if (acp3DsAction === PayPalAcp3dsAction.NewCard) {
                setFormErrors([
                  UIConstants.CARD_AUTHENTICATION_ERROR,
                  UIConstants.PROMPT_DIFFERENT_CARD,
                ]);
              } else {
                const response: PayPalAcpHostedFieldsResponseData = {
                  orderId: result.orderId,
                  creditCardLastFour: result.card?.last_digits ?? '****',
                  creditCardType:
                    result.card?.brand ?? fieldState.cards[0].niceType,
                };
                cart.paypalInfo.setIsVenmo(false);
                onCardValidated(response);
              }
            } else {
              setCardFormErrors(fieldState.fields);
            }
          } else {
            setFormErrors([
              UIConstants.CARD_PROCESSING_OFFLINE,
              UIConstants.PROMPT_DIFFERENT_PAYMENT_METHOD,
            ]);
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : UIConstants.PROMPT_DIFFERENT_PAYMENT_METHOD_OR_RETRY;
          setFormErrors([
            UIConstants.CARD_PROCESSING_GENERIC_ERROR,
            errorMessage,
          ]);
        }
        cart.setIsTokenizingCard(false);
      };

      const createOrder = async (): Promise<string> => {
        setFormErrors([]);
        try {
          const token = await rootStore.createPayPalOrder();
          return token;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          setFormErrors([
            UIConstants.CARD_PROCESSING_OFFLINE,
            UIConstants.PROMPT_DIFFERENT_PAYMENT_METHOD,
          ]);
        }
        return '';
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const renderCardFields = async (): Promise<void> => {
        const { paypal } = window;
        if (paypal?.HostedFields?.isEligible()) {
          const isCustomizationEnabled = configuration.isEnabled(
            'Feature_Checkout_Customization_Enabled'
          );
          hostedFields = await paypal.HostedFields.render({
            createOrder,
            styles: {
              input: {
                color: isCustomizationEnabled
                  ? configuration.styles.formAccentColor
                  : 'black',
                'font-size': '1rem',
                'font-family': isCustomizationEnabled
                  ? configuration.styles.primaryFont
                  : 'Lato, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial',
                padding: '15px 0px 0px 12px',
              },
            },
            fields: {
              number: {
                selector: '#adp-card-number',
              },
              expirationDate: {
                selector: '#adp-expiration-date',
              },
              cvv: {
                selector: '#adp-cvv',
              },
            },
          });

          hostedFields.on('focus', handleFieldStates);
          hostedFields.on('blur', handleFieldStates);
          hostedFields.on('empty', handleFieldStates);
          hostedFields.on('notEmpty', handleFieldStates);
          hostedFields.on('validityChange', handleFieldStates);
          rootStore.registerPayPalAcpOnSubmit(handleSubmitCard);
        }
      };

      useEffect(() => {
        if (!isPending && !isRendered) {
          setIsRendered(true);
          renderCardFields();
        }
      }, [isPending, isRendered, renderCardFields]);

      return (
        <div
          className="container"
          data-testid={PayPalAdvancedCardProcessingTestId}
        >
          <div className="flex flex-col space-y-4">
            <PayPalAdvancedCardProcessingField
              fieldName="adp-card-number"
              fieldLabel="Card Number"
              hasError={cardNumberState.hasError}
            />
            <div className="flex flex-row w-full justify-between">
              <PayPalAdvancedCardProcessingField
                fieldName="adp-expiration-date"
                fieldLabel="Exp MM/YYYY"
                hasError={expDateState.hasError}
              />
              <PayPalAdvancedCardProcessingField
                fieldName="adp-cvv"
                fieldLabel="CVV"
                hasError={cvvState.hasError}
              />
            </div>
            <div>
              <FormErrorLabel errors={formErrors} />
            </div>
          </div>
        </div>
      );
    }
  );

export default PayPalAdvancedCardProcessing;
